import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import { CheckCircle2, ShieldCheck, Sparkles, HelpCircle, FileText } from 'lucide-react';

const ListContext = React.createContext<{ isOrdered: boolean; index: number }>({ isOrdered: false, index: 0 });

interface ProductDescriptionRendererProps {
  content?: string;
  className?: string;
  showBadgeNotice?: boolean;
}

/**
 * Preprocesses raw description text so that bullets, headings, and lists
 * format cleanly as Markdown even if entered as plain text.
 */
function normalizeDescriptionContent(raw: string): string {
  if (!raw) return '';

  // Standardize line endings
  let text = raw.replace(/\r\n/g, '\n').replace(/\r/g, '\n');

  // Convert bullet indicators (•, ·, ✓, ✔, ►, ▪, ▫, +, ») at start of lines into "- "
  text = text.replace(/^[\t ]*[•·✓✔►▪▫\+\»][\t ]+/gm, '- ');

  // Convert "1) ", "(1) ", "1. " to markdown numbered list "1. "
  text = text.replace(/^[\t ]*\(?([0-9]+)\)[\t ]+/gm, '$1. ');

  const lines = text.split('\n');
  const processedLines: string[] = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();

    // Check if line looks like a section header (e.g., "Fitur Utama:", "Modul Keuangan:", "Kelengkapan:")
    // That is: short, ends with ':', and not already a markdown header or list
    if (
      trimmed.endsWith(':') &&
      trimmed.length > 2 &&
      trimmed.length < 70 &&
      !trimmed.startsWith('#') &&
      !trimmed.startsWith('-') &&
      !/^\d+\./.test(trimmed)
    ) {
      processedLines.push(`\n#### ${trimmed}\n`);
    } else {
      processedLines.push(line);
    }
  }

  return processedLines.join('\n');
}

export default function ProductDescriptionRenderer({ 
  content = '', 
  className = '',
  showBadgeNotice = true 
}: ProductDescriptionRendererProps) {
  if (!content || !content.trim()) {
    return (
      <div className="p-8 text-center bg-muted/30 rounded-2xl border border-dashed border-border text-foreground/50">
        <FileText className="h-8 w-8 mx-auto mb-2 opacity-40" />
        <p className="text-sm font-medium">Belum ada deskripsi lengkap untuk produk ini.</p>
      </div>
    );
  }

  const normalized = normalizeDescriptionContent(content);

  return (
    <div className={`space-y-6 ${className}`}>
      <div className="prose prose-slate dark:prose-invert max-w-none text-left">
        <ReactMarkdown
          remarkPlugins={[remarkGfm, remarkBreaks]}
          components={{
            p: ({ children }) => (
              <p className="text-base sm:text-lg text-foreground/80 leading-relaxed font-body text-left my-3.5 first:mt-0 last:mb-0">
                {children}
              </p>
            ),
            h1: ({ children }) => (
              <h2 className="text-xl sm:text-2xl font-bold text-foreground mt-8 mb-4 pb-2 border-b border-border flex items-center gap-2.5">
                <span className="w-2 h-6 bg-gold rounded-full shrink-0" />
                <span>{children}</span>
              </h2>
            ),
            h2: ({ children }) => (
              <h3 className="text-lg sm:text-xl font-bold text-foreground mt-7 mb-3 flex items-center gap-2.5">
                <span className="w-1.5 h-5 bg-gold rounded-full shrink-0" />
                <span>{children}</span>
              </h3>
            ),
            h3: ({ children }) => (
              <h4 className="text-base sm:text-lg font-bold text-foreground mt-6 mb-2.5 flex items-center gap-2 text-primary">
                <span className="w-1.5 h-4 bg-primary rounded-full shrink-0" />
                <span>{children}</span>
              </h4>
            ),
            h4: ({ children }) => (
              <div className="flex items-center gap-2.5 mt-6 mb-3 pt-3 border-t border-border/40 first:mt-0 first:pt-0">
                <div className="w-1.5 h-4 bg-gold rounded-full shrink-0" />
                <h4 className="text-sm sm:text-base font-bold text-foreground tracking-tight uppercase tracking-wider text-primary">
                  {children}
                </h4>
              </div>
            ),
            ul: ({ children }) => (
              <ul className="flex flex-col gap-2.5 my-3.5 pl-0 list-none">
                {children}
              </ul>
            ),
            ol: ({ children }) => (
              <ol className="flex flex-col gap-2.5 my-3.5 pl-0 list-none">
                {React.Children.map(children, (child, idx) => {
                  if (!React.isValidElement(child)) return child;
                  return (
                    <ListContext.Provider value={{ isOrdered: true, index: idx + 1 }}>
                      {child}
                    </ListContext.Provider>
                  );
                })}
              </ol>
            ),
            li: ({ children }) => {
              const { isOrdered, index } = React.useContext(ListContext);
              return (
                <li className="flex items-start gap-3 text-sm sm:text-base text-foreground/80 bg-muted/30 hover:bg-muted/50 transition-colors p-3 rounded-xl border border-border/50 leading-relaxed group">
                  {isOrdered ? (
                    <span className="h-5 w-5 rounded-md bg-gold/20 text-gold-foreground font-bold text-xs flex items-center justify-center shrink-0 mt-0.5 shadow-sm">
                      {index}
                    </span>
                  ) : (
                    <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0 mt-0.5 group-hover:scale-110 transition-transform" />
                  )}
                  <div className="flex-1 font-body leading-relaxed">{children}</div>
                </li>
              );
            },
            strong: ({ children }) => (
              <strong className="font-bold text-foreground">{children}</strong>
            ),
            blockquote: ({ children }) => (
              <blockquote className="border-l-4 border-gold bg-gold/5 p-4 rounded-r-xl italic text-foreground/85 my-4">
                {children}
              </blockquote>
            ),
            table: ({ children }) => (
              <div className="overflow-x-auto my-5 rounded-xl border border-border bg-card shadow-sm">
                <table className="w-full text-left border-collapse text-sm">
                  {children}
                </table>
              </div>
            ),
            thead: ({ children }) => (
              <thead className="bg-muted/60 border-b border-border text-foreground font-bold">
                {children}
              </thead>
            ),
            th: ({ children }) => (
              <th className="py-3 px-4 text-left font-bold text-foreground text-xs uppercase tracking-wider">
                {children}
              </th>
            ),
            td: ({ children }) => (
              <td className="py-3 px-4 border-b border-border/40 text-foreground/80 text-sm">
                {children}
              </td>
            ),
            code: ({ children }) => (
              <code className="bg-muted px-1.5 py-0.5 rounded text-xs font-mono text-primary font-semibold">
                {children}
              </code>
            ),
          }}
        >
          {normalized}
        </ReactMarkdown>
      </div>

      {showBadgeNotice && (
        <div className="mt-8 pt-6 border-t border-border/60">
          <div className="p-4 sm:p-5 rounded-2xl bg-gradient-to-r from-primary/5 via-gold/5 to-transparent border border-border/60 flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="h-11 w-11 rounded-xl bg-gold/15 text-gold-foreground flex items-center justify-center shrink-0 shadow-sm">
              <ShieldCheck className="h-6 w-6 text-gold" />
            </div>
            <div className="flex-1">
              <h5 className="font-bold text-foreground text-sm sm:text-base flex items-center gap-2">
                Format Terverifikasi & Sesuai Regulasi
                <span className="text-[10px] bg-emerald-500/10 text-emerald-600 font-bold px-2 py-0.5 rounded-full border border-emerald-500/20">
                  Siap Pakai
                </span>
              </h5>
              <p className="text-xs sm:text-sm text-foreground/60 mt-0.5 leading-relaxed">
                Disusun oleh tim profesional bersertifikat dengan pengalaman lebih dari 10 tahun di industri konstruksi & tender LPSE Indonesia.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
